import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon } from "../components/icons";
import { Avatar, Badge, Btn, BtnInner, Empty, ErrorNote, Field, Loading, Modal, PageHead, ShownOnce } from "../components/kit";
import { api, identity, type Account, type Role } from "../lib/api";
import { ago } from "../lib/format";
import { useAction, useResource } from "../lib/hooks";

const FILTERS = [
  { value: "", label: "Everyone" },
  { value: "admins", label: "Administrators" },
  { value: "disabled", label: "Disabled" },
];

interface Page {
  accounts: Account[];
  total: number;
  offset: number;
  limit: number;
}

export function AccountBadges({ account }: { account: Account }) {
  return (
    <span className="badges">
      {account.role === "admin" && <Badge tone="accent">Admin</Badge>}
      {account.disabled && <Badge tone="danger">Disabled</Badge>}
      {account.two_step ? <Badge tone="ok">Two-step</Badge> : <Badge>Password only</Badge>}
    </span>
  );
}

export function AccountsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const filter = params.get("filter") ?? "";
  const offset = Number(params.get("offset") ?? 0) || 0;
  const [search, setSearch] = useState(q);
  const wantsNew = params.get("new") === "1";
  const [creating, setCreating] = useState(wantsNew);

  // The address can change from outside the page, from the sidebar's search
  // or its new-account button: follow it rather than writing the old value back.
  const [seen, setSeen] = useState({ q, wantsNew });
  if (seen.q !== q || seen.wantsNew !== wantsNew) {
    setSeen({ q, wantsNew });
    if (seen.q !== q) setSearch(q);
    if (wantsNew && !seen.wantsNew) setCreating(true);
  }

  // Typing updates the address a moment later, which reloads the list.
  useEffect(() => {
    if (search === q) return;
    const t = setTimeout(() => {
      setParams((p) => {
        if (search) p.set("q", search);
        else p.delete("q");
        p.delete("offset");
        return p;
      });
    }, 250);
    return () => clearTimeout(t);
  }, [search, q, setParams]);

  const query = new URLSearchParams();
  if (q) query.set("q", q);
  if (filter) query.set("filter", filter);
  if (offset) query.set("offset", String(offset));
  const { data, error, loading, reload } = useResource<Page>(`/accounts?${query}`);

  const setFilter = (value: string) =>
    setParams((p) => {
      if (value) p.set("filter", value);
      else p.delete("filter");
      p.delete("offset");
      return p;
    });
  const page = (to: number) =>
    setParams((p) => {
      if (to) p.set("offset", String(to));
      else p.delete("offset");
      return p;
    });

  return (
    <>
      <PageHead
        title="Accounts"
        lede="People who sign in at id.mashriqain.com and, through it, to every portal that lets them in. There is no public sign-up."
      >
        <Btn variant="primary" icon="plus" onClick={() => setCreating(true)}>
          New account
        </Btn>
      </PageHead>

      <ErrorNote error={error} />
      <section className="table-card">
        <div className="toolbar">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" placeholder="Search by name, email or phone" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search accounts" />
          </label>
          <div className="tabs" role="tablist" aria-label="Show">
            {FILTERS.map((f) => (
              <button key={f.value} type="button" role="tab" aria-selected={filter === f.value} onClick={() => setFilter(f.value)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrap" data-loading={loading || undefined}>
          {!data ? (
            <Loading />
          ) : data.accounts.length === 0 ? (
            <Empty title={q ? `Nobody matches “${q}”.` : "No accounts here."} />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                  <th>Created</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/accounts/${a.id}`} className="row-person">
                        <Avatar name={a.name} />
                        <span className="row-link">
                          <span className="row-title">{a.name}</span>
                          <span className="row-sub">{[a.email, a.phone].filter(Boolean).join(" · ")}</span>
                        </span>
                      </Link>
                    </td>
                    <td>
                      <AccountBadges account={a} />
                    </td>
                    <td className="muted" data-label="Last sign-in">{a.last_sign_in_at ? ago(a.last_sign_in_at) : "Never"}</td>
                    <td className="muted hide-phone">{ago(a.created_at)}</td>
                    <td className="row-go">
                      <Link to={`/accounts/${a.id}`} className="go" aria-label={`Open ${a.name}`}>
                        →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {data && data.total > data.limit && (
          <div className="pager">
            <Btn disabled={offset === 0} onClick={() => page(Math.max(0, offset - data.limit))}>
              Newer
            </Btn>
            <span className="muted">
              {offset + 1}–{Math.min(offset + data.limit, data.total)} of {data.total}
            </span>
            <Btn disabled={offset + data.limit >= data.total} onClick={() => page(offset + data.limit)}>
              Older
            </Btn>
          </div>
        )}
      </section>

      <NewAccount
        open={creating}
        onClose={() => {
          setCreating(false);
          if (params.has("new"))
            setParams((p) => {
              p.delete("new");
              return p;
            });
        }}
        onCreated={reload}
      />
    </>
  );
}

function NewAccount({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [made, setMade] = useState<{ account: Account; password?: string } | null>(null);
  const { busy, error, run, clear } = useAction();

  const close = () => {
    setEmail("");
    setPhone("");
    setName("");
    setRole("member");
    setMade(null);
    clear();
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const out = await run(() => api<{ account: Account; password?: string }>("POST", "/accounts", { email, phone, name, role }));
    if (out) {
      setMade(out);
      onCreated();
    }
  };

  return (
    <Modal open={open} title={made ? "Account created" : "New account"} onClose={close}>
      {made ? (
        <>
          <p>
            <strong>{made.account.name}</strong> can now sign in at id.mashriqain.com as <strong>{identity(made.account)}</strong>
            {!made.password && ", with a code sent by SMS"}.
          </p>
          {made.password && (
            <ShownOnce label="First password" value={made.password}>
              This is shown once. Send it privately; they should change it at id.mashriqain.com and turn on two-step sign-in.
            </ShownOnce>
          )}
          <div className="modal-actions">
            <Link to={`/accounts/${made.account.id}`} className="btn" data-variant="ghost" onClick={close}>
              <BtnInner>Open account</BtnInner>
            </Link>
            <Btn variant="primary" onClick={close}>
              Done
            </Btn>
          </div>
        </>
      ) : (
        <form className="form" onSubmit={submit}>
          <Field label="Name" error={error?.fields.name}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoFocus />
          </Field>
          <Field label="Email" hint="Signs in with a password." error={error?.fields.email}>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required={!phone} autoComplete="off" />
          </Field>
          <Field label="Mobile number" hint="Signs in with a code by SMS. Give an email, a number or both." error={error?.fields.phone}>
            <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required={!email} autoComplete="off" placeholder="01712345678" />
          </Field>
          <Field label="Role" hint="Administrators can open this panel and change any account.">
            <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="member">Member</option>
              <option value="admin">Administrator</option>
            </select>
          </Field>
          {error && !error.fields.name && !error.fields.email && !error.fields.phone && <ErrorNote error={error} />}
          <p className="muted small">With an email, a password is generated and shown once when the account is made.</p>
          <div className="modal-actions">
            <Btn onClick={close}>Cancel</Btn>
            <Btn variant="primary" type="submit" disabled={busy}>
              {busy ? "Creating…" : "Create account"}
            </Btn>
          </div>
        </form>
      )}
    </Modal>
  );
}
